import { Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import {
  AlertBoxOptions,
  AlertType,
  PrimaryButton
} from "@core/models/alert-box-types";
import { TranslateService } from "@ngx-translate/core";
import { AlertDialogComponent } from "@shared/components/alert-dialog/alert-dialog.component";

interface AlertBoxOptionsProps extends Partial<AlertBoxOptions> {
  title: string;
  description: string;
  leftButtonTitle: string;
}

@Injectable({
  providedIn: "root"
})
export class AlertBoxService {
  constructor(
    private translateService: TranslateService,
    private dialog: MatDialog
  ) {}

  openAlertBox(
    params: AlertBoxOptionsProps
  ): MatDialogRef<AlertDialogComponent, any> {
    return this.dialog.open<AlertDialogComponent, AlertBoxOptions>(
      AlertDialogComponent,
      {
        disableClose: true,
        data: this.createAlertBoxOptions(params),
        autoFocus: false,
        panelClass: "bci-web-core-alert-pane"
      }
    );
  }

  openConfirmationGenericActionAlertBox(params?: Partial<AlertBoxOptions>) {
    let alertOptions: AlertBoxOptionsProps = {
      type: AlertType[Symbol.hasInstance],
      primaryButton: PrimaryButton.LeftButton,
      title: this.translateService.instant(
        "alert-box.confirm-generic-action-modal.title"
      ),
      description: this.translateService.instant(
        "alert-box.confirm-generic-action-modal.description"
      ),
      leftButtonTitle: this.translateService.instant("option-yes"),
      rightButtonTitle: this.translateService.instant("option-no")
    };

    if (params) {
      alertOptions = { ...alertOptions, ...params };
    }

    return this.openAlertBox(this.createAlertBoxOptions(alertOptions));
  }

  openConfirmationCompleteActionAlertBox(
    titleParam: string,
    descriptionParam: string,
    leftButtonTitleParam: string,
    rightButtonTitleParam: string
  ) {
    let leftButtonTitleCustom, rightButtonTitleCustom: string;
    if (leftButtonTitleParam != "") {
      leftButtonTitleCustom = leftButtonTitleParam;
    } else {
      leftButtonTitleCustom = "input-save";
    }
    if (rightButtonTitleParam != "") {
      rightButtonTitleCustom = rightButtonTitleParam;
    } else {
      rightButtonTitleCustom = "input-cancel";
    }
    const params: AlertBoxOptionsProps = {
      type: AlertType[Symbol.hasInstance],
      primaryButton: PrimaryButton.LeftButton,
      title: this.translateService.instant(titleParam),
      description: this.translateService.instant(descriptionParam),
      leftButtonTitle: this.translateService.instant(leftButtonTitleCustom),
      rightButtonTitle: this.translateService.instant(rightButtonTitleCustom)
    };

    return this.openAlertBox(this.createAlertBoxOptions(params));
  }

  openConfirmationDeleteActionAlertBox(
    titleParam: string,
    descriptionParam: string,
    actionKey: "input-delete" | "input-remove" = "input-delete"
  ) {
    const actionTranslated = this.translateService.instant(actionKey);
    const params: AlertBoxOptionsProps = {
      type: AlertType[Symbol.hasInstance],
      primaryButton: PrimaryButton.LeftButton,
      title: this.translateService.instant(
        "alert-box.confirm-delete-action-modal.title",
        { titleParam, action: actionTranslated }
      ),
      description: this.translateService.instant(
        "alert-box.confirm-delete-action-modal.description",
        { descriptionParam }
      ),
      leftButtonTitle: this.translateService.instant("input-remove"),
      rightButtonTitle: this.translateService.instant("input-cancel")
    };

    return this.openAlertBox(this.createAlertBoxOptions(params));
  }

  private createAlertBoxOptions(params: AlertBoxOptionsProps): AlertBoxOptions {
    const alertBoxOptions: AlertBoxOptions = {
      type: AlertType[Symbol.hasInstance],
      ...params,
      primaryButton: PrimaryButton.LeftButton
    };
    return alertBoxOptions;
  }
}
